/**
 * Freehand draw tool (F-121 canvas; new element type per docs/08 §2).
 *
 * While draw mode is on, a screen-space capture surface fills the board canvas
 * (same containing block as the F-27 toolbar and comment overlays): a left drag
 * paints a stroke, previewed live, and on release becomes a `drawing` element
 * that BELONGS to wherever it started — the cell under the stroke's first point,
 * or the bare board (geometry.ts `drawStartAnchor`). The stroke may cross cell
 * lines; ownership is fixed at the start.
 *
 * Points are captured in BOARD units (screenToBoard), so a mid-stroke pan/zoom
 * keeps the line anchored; the preview converts them back through boardToScreen
 * each render (it subscribes to the camera). Esc exits the tool. All writes go
 * through session (shared accessors, LOCAL_ORIGIN → one undoable element).
 */
import { boardToScreen, screenToBoard, type Point } from "@kola/shared";
import { useEffect, useRef, useState, type RefObject } from "react";
import { useStore } from "zustand";

import { safeSetPointerCapture, type CanvasStore } from "../../core/canvas.js";
import type { BoardSession } from "../../core/session.js";
import type { GridLayoutSnapshot } from "../board/GridTable.js";
import { appendPoint, drawStartAnchor, strokeBounds, toLocalPath } from "./geometry.js";
import type { DrawUiStore } from "./store.js";

export interface DrawingLayerProps {
  session: BoardSession;
  canvas: CanvasStore;
  draw: DrawUiStore;
  containerRef: RefObject<HTMLDivElement | null>;
  /** The grid's latest layout pass — start-point cell hit-testing (F-36). */
  layout: GridLayoutSnapshot | null;
}

/** Gate: subscribe to `mode` only, so nothing here re-renders on pan when off. */
export function DrawingLayer(props: DrawingLayerProps) {
  const mode = useStore(props.draw, (s) => s.mode);
  useEffect(() => {
    if (!mode) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") props.draw.getState().setMode(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mode, props.draw]);
  if (!mode) return null;
  return <DrawingCapture {...props} />;
}

function DrawingCapture({ session, canvas, draw, containerRef, layout }: DrawingLayerProps) {
  const camera = useStore(canvas, (s) => s.camera);
  const viewport = useStore(canvas, (s) => s.viewport);
  const color = useStore(draw, (s) => s.color);
  const thickness = useStore(draw, (s) => s.thickness);

  // In-progress stroke in BOARD units. The ref is the source of truth for the
  // commit; the state mirror only drives the preview re-render.
  const strokeRef = useRef<Point[] | null>(null);
  const [stroke, setStroke] = useState<Point[] | null>(null);
  const pointerIdRef = useRef<number | null>(null);

  const toBoard = (clientX: number, clientY: number): Point => {
    const rect = containerRef.current?.getBoundingClientRect();
    const left = rect?.left ?? 0;
    const top = rect?.top ?? 0;
    return screenToBoard(canvas.getState().camera, { x: clientX - left, y: clientY - top });
  };

  const onPointerDown = (event: React.PointerEvent) => {
    if (event.button !== 0) return; // left only — middle/right stay pan/menu
    // A press while a stroke is already down must NOT restart it. A mouse
    // never does this; a tablet pen does — the barrel button, a driver
    // re-report, or a second contact all arrive as a fresh pointerdown, and
    // resetting `strokeRef` there threw away everything drawn so far (the
    // stroke visibly vanished mid-draw and committed a stray dot instead).
    if (strokeRef.current !== null) return;
    event.preventDefault();
    event.stopPropagation();
    const start = toBoard(event.clientX, event.clientY);
    strokeRef.current = [start];
    setStroke([start]);
    pointerIdRef.current = event.pointerId;
    safeSetPointerCapture(event.currentTarget as HTMLElement, event.pointerId);
  };

  /** Discard the in-progress stroke with NO element commit. The ONLY reason to
   * throw a drawn stroke away: a second finger landed, so the camera gesture
   * owns the pointer (canvas.ts). Every other end — pointerup, pointercancel,
   * a capture stolen mid-stroke — commits what the user drew. */
  const cancelStroke = () => {
    strokeRef.current = null;
    pointerIdRef.current = null;
    setStroke(null);
  };

  const onPointerMove = (event: React.PointerEvent) => {
    if (pointerIdRef.current !== event.pointerId || strokeRef.current === null) return;
    if (canvas.getState().pinching) {
      cancelStroke(); // two fingers zoom/pan — they never draw
      return;
    }
    appendPoint(strokeRef.current, toBoard(event.clientX, event.clientY));
    setStroke([...strokeRef.current]);
  };

  const finishStroke = () => {
    const points = strokeRef.current;
    strokeRef.current = null;
    pointerIdRef.current = null;
    setStroke(null);
    if (points === null || points.length === 0) return;
    const t = draw.getState().thickness;
    const box = strokeBounds(points, t);
    if (box === null) return;
    const start = points[0] as Point;
    const anchor = drawStartAnchor(start, box, layout);
    session.createDrawingElement(
      box.x,
      box.y,
      box.w,
      box.h,
      { d: toLocalPath(points, box), color: draw.getState().color, thickness: t },
      anchor,
    );
  };

  const onPointerUp = (event: React.PointerEvent) => {
    if (pointerIdRef.current !== event.pointerId) return;
    finishStroke();
  };

  const onLostPointerCapture = (event: React.PointerEvent) => {
    // On a normal release onPointerUp has already run and the id guard makes
    // this a no-op. Reaching it with a stroke still live means capture was
    // taken from us WITHOUT a pointerup/pointercancel.
    if (pointerIdRef.current !== event.pointerId) return;
    // Two fingers ALWAYS mean the camera (canvas.ts), and that gesture steals
    // the pointer deliberately — that stroke was never meant to exist.
    if (canvas.getState().pinching) {
      cancelStroke();
      return;
    }
    // Anything else is the browser taking the pointer away mid-stroke, which a
    // tablet pen provokes far more often than a mouse (proximity flicker, a
    // driver re-report, the pen crossing the tablet's mapped edge). What the
    // user drew is real and already on screen: COMMIT it. Silently dropping it
    // was the one path in this file that could destroy a finished stroke.
    finishStroke();
  };

  // Live preview: board points → screen for THIS camera; thickness scales too.
  const previewPoints =
    stroke !== null && stroke.length > 0
      ? stroke.map((p) => {
          const s = boardToScreen(camera, p);
          return `${s.x},${s.y}`;
        })
      : null;
  const first = stroke?.[0];
  const firstScreen = first !== undefined ? boardToScreen(camera, first) : null;

  return (
    <div
      className="drawing-capture"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onLostPointerCapture={onLostPointerCapture}
    >
      {previewPoints !== null && (
        <svg
          className="drawing-preview"
          width={viewport.width}
          height={viewport.height}
          style={{ overflow: "visible", pointerEvents: "none" }}
          aria-hidden="true"
        >
          {previewPoints.length === 1 && firstScreen !== null ? (
            <circle
              cx={firstScreen.x}
              cy={firstScreen.y}
              r={(thickness * camera.zoom) / 2}
              fill={color}
            />
          ) : (
            <polyline
              points={previewPoints.join(" ")}
              fill="none"
              stroke={color}
              strokeWidth={thickness * camera.zoom}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>
      )}
    </div>
  );
}
